/**
 * 睡眠の推移（日ごとの睡眠時間・睡眠の時間帯・HRV/安静時心拍・傾向）
 */

import { Lightbulb } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { SleepEpisodeSummary } from '@/api/types/sleep'
import { Skeleton } from '@/components/ui/skeleton'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'
import { useSleepStore } from '@/store/useSleepStore'

import { formatMinutes } from '../format'
import { PatternCard } from './pattern-card'
import { TextList } from './plan-sections'

const RANGES = [14, 30, 90] as const
const DAY_MS = 24 * 60 * 60 * 1000
// JST の 0 時を UTC で表すためのずれ
const JST_OFFSET_MS = 9 * 60 * 60 * 1000

const tooltipStyle = { backgroundColor: 'var(--popover)', border: '1px solid var(--border)', fontSize: 12 }

const shortDate = (isoDate: string) => {
  const [, m, d] = isoDate.split('-')
  return `${Number(m)}/${Number(d)}`
}

type TimelineBar = { left: number; width: number; nap: boolean; id: number }

/** 各睡眠を JST の日ごとの行（0〜24時）に分けて置く */
const buildTimeline = (episodes: SleepEpisodeSummary[], days: number) => {
  const todayStart = Math.floor((Date.now() + JST_OFFSET_MS) / DAY_MS) * DAY_MS - JST_OFFSET_MS
  const rows = Array.from({ length: days }, (_, i) => {
    const dayStart = todayStart - (days - 1 - i) * DAY_MS
    const label = new Date(dayStart + JST_OFFSET_MS).toISOString().slice(0, 10)
    return { dayStart, label, bars: [] as TimelineBar[] }
  })
  for (const e of episodes) {
    const start = parseUtcDate(e.sleep_start).getTime()
    const end = parseUtcDate(e.sleep_end).getTime()
    for (const row of rows) {
      const s = Math.max(start, row.dayStart)
      const t = Math.min(end, row.dayStart + DAY_MS)
      if (t <= s) continue
      row.bars.push({
        id: e.id,
        nap: e.kind === 'nap',
        left: ((s - row.dayStart) / DAY_MS) * 100,
        width: ((t - s) / DAY_MS) * 100,
      })
    }
  }
  return rows
}

const SleepTimeline = ({ episodes, days }: { episodes: SleepEpisodeSummary[]; days: number }) => {
  const rows = useMemo(() => buildTimeline(episodes, days), [episodes, days])
  return (
    <section className="rounded-xl border bg-card p-4 shadow-sm">
      <p className="mb-2 text-sm font-medium">眠っていた時間帯</p>
      <div className="flex pl-10 text-[10px] text-muted-foreground">
        {[0, 6, 12, 18].map((h) => (
          <span key={h} className="flex-1">
            {h}時
          </span>
        ))}
      </div>
      <div className="space-y-px">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center">
            <span className="w-10 shrink-0 text-[10px] text-muted-foreground tabular-nums">{shortDate(row.label)}</span>
            <div className="relative h-2.5 flex-1 rounded-sm bg-muted/40">
              {row.bars.map((bar) => (
                <div
                  key={`${bar.id}-${bar.left}`}
                  className={cn('absolute h-full rounded-sm', bar.nap ? 'bg-amber-400' : 'bg-indigo-500')}
                  style={{ left: `${bar.left}%`, width: `${Math.max(bar.width, 0.5)}%` }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="inline-block size-2 rounded-sm bg-indigo-500" />
          睡眠
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block size-2 rounded-sm bg-amber-400" />
          昼寝
        </span>
      </div>
    </section>
  )
}

export const SleepTrends = () => {
  const [days, setDays] = useState<number>(30)
  const { trends, isTrendsLoading, fetchTrends } = useSleepStore()
  const insights = useSleepStore((s) => s.plan?.insights)

  useEffect(() => {
    void fetchTrends(days)
  }, [days, fetchTrends])

  const chartDays = useMemo(
    () =>
      (trends?.days ?? []).map((d) => ({
        ...d,
        label: shortDate(d.date),
        main_h: d.main_min / 60,
        nap_h: d.nap_min / 60,
      })),
    [trends?.days],
  )

  return (
    <div className="space-y-4">
      <div className="flex gap-1">
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setDays(r)}
            className={cn(
              'rounded-md border px-3 py-1 text-xs',
              days === r ? 'border-indigo-500 bg-indigo-500/10 font-medium' : 'bg-card',
            )}
          >
            {r}日
          </button>
        ))}
      </div>

      {isTrendsLoading && !trends ? (
        <Skeleton className="h-64" />
      ) : trends ? (
        <>
          <section className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="mb-3 grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[11px] text-muted-foreground">就寝の中心</p>
                <p className="font-semibold tabular-nums">{trends.regularity.bedtime_median ?? '--'}</p>
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground">起床の中心</p>
                <p className="font-semibold tabular-nums">{trends.regularity.wake_median ?? '--'}</p>
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground">ばらつき（14日）</p>
                <p className="font-semibold tabular-nums">
                  {trends.regularity.midpoint_sd_hours !== null ? `±${trends.regularity.midpoint_sd_hours}時間` : '--'}
                </p>
              </div>
            </div>
            <p className="mb-1 text-sm font-medium">1日の睡眠時間</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartDays} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} unit="h" />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, name) => [formatMinutes(Number(v) * 60), name === 'main_h' ? '睡眠' : '昼寝']}
                />
                <ReferenceLine
                  y={trends.need_min / 60}
                  stroke="hsl(142 70% 40%)"
                  strokeDasharray="4 4"
                  label={{ value: '必要量', fontSize: 10, position: 'insideTopRight' }}
                />
                <Bar dataKey="main_h" stackId="s" fill="hsl(239 84% 67%)" isAnimationActive={false} />
                <Bar dataKey="nap_h" stackId="s" fill="hsl(43 96% 56%)" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </section>

          <SleepTimeline episodes={trends.episodes} days={days} />

          <section className="rounded-xl border bg-card p-4 shadow-sm">
            <p className="mb-1 text-sm font-medium">HRV と安静時心拍</p>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartDays} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="hrv" tick={{ fontSize: 10 }} domain={['dataMin - 5', 'dataMax + 5']} />
                <YAxis
                  yAxisId="rhr"
                  orientation="right"
                  tick={{ fontSize: 10 }}
                  domain={['dataMin - 3', 'dataMax + 3']}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Line
                  yAxisId="hrv"
                  dataKey="hrv_avg"
                  name="HRV (ms)"
                  stroke="hsl(262 83% 58%)"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="rhr"
                  dataKey="resting_hr"
                  name="安静時心拍"
                  stroke="hsl(0 75% 55%)"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </section>

          <PatternCard patterns={trends.patterns} />
          {insights && insights.length > 0 && (
            <section className="rounded-xl border bg-card p-4 shadow-sm">
              <TextList title="あなたの記録から" icon={Lightbulb} items={insights} />
            </section>
          )}
        </>
      ) : null}
    </div>
  )
}
